import { useParams, Link } from 'react-router-dom';
import { useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/base/SEOHead';
import { articles } from '@/mocks/comics';

const siteUrl = 'http://iitzgd.cn/';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === Number(id));

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  if (!article) {
    return (
      <>
        <Navbar />
        <div className="pt-20 pb-10 text-center">
          <p className="text-foreground-500">文章不存在</p>
        </div>
        <Footer />
      </>
    );
  }

  const pageUrl = `${siteUrl}/article/${id}`;
  const relatedArticles = articles.filter((a) => a.id !== article.id).slice(0, 3);

  const schema = [
    {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: article.title,
      description: article.excerpt,
      author: { '@type': 'Person', name: article.author },
      datePublished: article.date,
      url: pageUrl,
      image: article.image,
      publisher: {
        '@type': 'Organization',
        name: '韩漫大全',
        url: siteUrl,
      },
      inLanguage: 'zh-CN',
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: siteUrl },
        { '@type': 'ListItem', position: 2, name: '文章资讯', item: `${siteUrl}/` },
        { '@type': 'ListItem', position: 3, name: article.title, item: pageUrl },
      ],
    },
  ];

  return (
    <>
      <SEOHead
        title={`${article.title}_韩漫资讯_韩漫大全`}
        description={article.excerpt}
        keywords={`${article.category},韩国漫画,韩漫资讯,${article.title},漫画推荐`}
        canonicalUrl={pageUrl}
        ogImage={article.image}
        ogType="article"
        schemaData={schema}
      />
      <Navbar />
      <main>
        <div className="pt-16">
          <Breadcrumb
            items={[
              { label: '首页', to: '/' },
              { label: '文章资讯' },
              { label: article.title },
            ]}
          />

          <article className="py-8 md:py-12 bg-background-50">
            <div className="mx-auto px-4 md:px-6 max-w-4xl">
              <div className="mb-8">
                <span className="inline-block px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium mb-3">
                  {article.category}
                </span>
                <h1 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-4">{article.title}</h1>
                <div className="flex items-center gap-3 text-xs text-foreground-500">
                  <span>{article.author}</span>
                  <span>·</span>
                  <span>{article.date}</span>
                </div>
              </div>

              <div className="rounded-lg overflow-hidden mb-8">
                <img
                  src={article.image}
                  alt={article.title}
                  title={article.title}
                  className="w-full h-auto object-cover"
                />
              </div>

              <div className="prose prose-sm max-w-none">
                <p className="text-foreground-700 leading-relaxed text-sm md:text-base">
                  {article.content}
                </p>
                <p className="text-foreground-600 leading-relaxed text-sm mt-4">
                  随着韩国漫画产业的持续发展，越来越多的优秀作品涌现出来。韩漫以其独特的竖版阅读模式、精美的画风和引人入胜的剧情，吸引了全球范围内的读者。无论是初次接触韩漫的新手，还是资深的韩漫爱好者，都能在这个丰富多彩的漫画世界中找到属于自己的乐趣。
                </p>
                <p className="text-foreground-600 leading-relaxed text-sm mt-4">
                  韩漫大全致力于为中文读者提供最新、最全、最优质的韩国漫画资源。我们持续关注韩国漫画行业的动态，为您带来第一手的漫画资讯和深度分析。感谢您的阅读，欢迎继续探索更多精彩内容。
                </p>
              </div>

              <div className="mt-10 pt-8 border-t border-background-200">
                <div className="flex flex-wrap gap-2">
                  <span className="text-xs text-foreground-500">标签：</span>
                  <span className="px-2 py-0.5 rounded-full bg-background-100 text-xs text-foreground-600 whitespace-nowrap">韩国漫画</span>
                  <span className="px-2 py-0.5 rounded-full bg-background-100 text-xs text-foreground-600 whitespace-nowrap">{article.category}</span>
                  <span className="px-2 py-0.5 rounded-full bg-background-100 text-xs text-foreground-600 whitespace-nowrap">漫画资讯</span>
                </div>
              </div>
            </div>
          </article>

          {relatedArticles.length > 0 && (
            <section className="py-10 md:py-14 bg-background-100">
              <div className="mx-auto px-4 md:px-6 max-w-4xl">
                <h2 className="font-heading text-xl text-foreground-900 mb-6">相关文章</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                  {relatedArticles.map((rel) => (
                    <Link
                      key={rel.id}
                      to={`/comic/${rel.id}`}
                      className="group block rounded-lg overflow-hidden bg-background-50 border border-background-200/70 hover:border-primary-300 transition-all duration-300"
                    >
                      <div className="aspect-video overflow-hidden bg-background-200">
                        <img
                          src={rel.image}
                          alt={rel.title}
                          title={rel.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-4">
                        <span className="text-xs text-accent-500">{rel.category}</span>
                        <h3 className="font-heading text-sm text-foreground-900 group-hover:text-primary-500 transition-colors mt-1 line-clamp-2">
                          {rel.title}
                        </h3>
                        <p className="text-xs text-foreground-500 mt-2">{rel.date}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </section>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}
